import { ViewType, ViewObject } from '../base';

import { AppConstants } from '@unpispas/upp-defs';
import { PriceInfo } from '@unpispas/upp-defs';

import { dataService } from '../../data';
import { Discount } from '../objects/discount';
import { DiscountPeriod } from '../objects/discountperiod';
import { DiscountProduct } from '../objects/discountproduct';

import { PlaceView } from './place';
import { TicketView } from './ticket';
import { ProductView } from './product';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _DiscountView extends ViewObject<Discount> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get discount(): Discount{
        return this.object;
    }
             
    constructor(object: Discount, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _validperiods: Array <DiscountPeriod> | null = null;
    private _validapplies: Array <DiscountProduct> | null = null;
    private _validproducts: Array <ProductView> | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('DISCOUNT')){
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCT')){
            this._validproducts = null;
            this.DoRefreshView();
        }

        if (targets.includes('DISCOUNTPERIOD')){
            this._validperiods = null;
            this.DoRefreshView();
        }

        if (targets.includes('DISCOUNTPRODUCT')){
            this._validapplies = null;
            this._validproducts = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

         // update registrations when children change
        this._subscriptions.push(
            this.discount.OnChildChanged.subscribe(() => {
                const self = weakref.deref();
                if (self) self.doRegister();
            })
        );

        // register to updates on contained items             
        this._subscriptions.push(
            this.discount.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }
            })
        )

        for(const _product of this.products){
            this._subscriptions.push(
                _product.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }
                })
            );
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/
    
    get place(): PlaceView | null {
        return this.data.alives.get(this.discount.place) as PlaceView;
    }

    get periods(): Array <DiscountPeriod> {
        if (this._validperiods == null){
            const _periods: Array <DiscountPeriod> = []; 

            for(const _period of this.discount.periods){
                if (_period.IsValid){
                    _periods.push(_period);
                }
            }

            this._validperiods = _periods;
        }

        return this._validperiods;
    }

    get applies(): Array <DiscountProduct> {
        if (this._validapplies == null){
            const _applies: Array <DiscountProduct> = []; 

            for(const _discountproduct of this.discount.products){
                if (_discountproduct.IsValid){
                    _applies.push(_discountproduct);
                }
            }

            this._validapplies = _applies;
        }

        return this._validapplies;
    }

    private _groupproducts(group: ProductView): Array <ProductView> {
        const _products: Array <ProductView> = [];

        if (!group.IsGroup){
            return [ group ];
        }

        for(const product of group.products){
            if (product.IsGroup){
                _products.push(...this._groupproducts(product));
            }
            else {  // trivial case
                _products.push(product)
            }
        }

        return _products;
    }

    get products(): Array <ProductView> {
        if (this._validproducts == null){
            const _products: Array <ProductView> = [];

            for(const _discountproduct of this.discount.products){
                if (_discountproduct.IsValid){
                    const _product = _discountproduct.product?.last;
                    if (_product){
                        const _productview = this.alives.get(_product) as ProductView;
                        if (_productview){
                            _products.push(...this._groupproducts(_productview));
                        }
                    }
                }
            }

            this._validproducts = _products;
        }

        return this._validproducts;
    }

    /**********************************/
    /* PRODUCT TYPE                   */
    /**********************************/
    
    get IsOffer(): boolean {
        return false;
    }

    get IsMenu(): boolean {
        return false;
    }

    get IsDiscount(): boolean {
        return true;
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/
    
    get IsActive(): boolean {
        if (!this.discount.IsValid){
            return false;
        }

        if (this.discount.allday){
            return true;
        }

        const _now = new Date();
        for (const period of this.periods){
            if (period.IsActiveAt(_now)){
                return true;
            }
        }

        return false;
    }    

    /**********************************/
    /* DISCOUNT APPLICATION           */
    /**********************************/

    AppliesTo(ticket: TicketView): boolean {
        let _applicable = true;

        // cannot apply more than one non-cumulative discount
        if (_applicable){
            _applicable = !ticket.discounts.some((discount) => {
                return discount.discount && !discount.discount.cumulative;
            });    
        }

        // cannot apply discount if is cumulative and other discounts applies
        if (_applicable){
            if (!this.discount.cumulative){
                _applicable = (ticket.discounts.length == 0);
            }
        }

        // the discount must be currently active
        if (_applicable){
            _applicable = this.IsActive;
        }

        // at list one applied product must be present
        if (_applicable){
            if (!this.discount.allprd){
                const _appliesto = this.products;
                _applicable = ticket.products.some((ticketproduct) =>
                    !!ticketproduct.product && _appliesto.includes(ticketproduct.product)
                );
            };
        }
  
        return _applicable;
    }

    ChargeAmount(ticket: TicketView): number {
        if (this.discount.type == 'TICKETFIXED'){
            return this._fixedAmount(ticket);
        }

        if (this.discount.type == 'TICKETPRCNT'){
            return this._prcntAmount(ticket);
            
        }

        return 0;   // invalid discount type;
    }

    RefundAmount(ticket: TicketView) : number {
        void ticket;
        return 0;   // TODO
    }

    /**********************************/
    /* PRIVATE METHODS                */
    /**********************************/    
    
    private _ticketPrice(ticket: TicketView): number {
        const _calcpriceinfo = new PriceInfo();

        for(const _ticketproduct of ticket.products){            
            const _info = _ticketproduct.calcinfo;
            const _charge: number = _info.charge || 0;
            const _amount: number = _info.amount || 0;

            const _p: number = _charge * _amount;
            const _r: number = _ticketproduct.taxrate ?? AppConstants.defaultTaxRate;
            
            _calcpriceinfo.Add(_p, _r); 
        }

        // apply extras into the final price
        for(const _ticketextra of ticket.extras){
            const _info = _ticketextra.calcinfo;
            const _p: number = _info.charge - _info.refund;
            const _r: number = _ticketextra.taxrate ?? AppConstants.defaultTaxRate;

            _calcpriceinfo.Add(_p, _r);                 
        }

        // apply other discounts into the final price
        const _discountview = this.alives.get(this.discount) as DiscountView;
        for (const _ticketdiscount of ticket.discounts){
            if (_ticketdiscount.discount == _discountview){
                continue;   // do not consider this discount
            }

            _calcpriceinfo.Del(_ticketdiscount.charge || 0);
        }        

        return _calcpriceinfo.price.val;        
    }

    private _fixedAmount(ticket: TicketView): number {
        if (this.discount.allprd){
            return Math.min((ticket.price || 0), (this.discount.value || 0));
        }
        else {
            const _appliesto = this.products;

            let _discount = 0;  // the discount value is applied per affected product
            for (const ticketproduct of ticket.products){
                if (ticketproduct.product && _appliesto.includes(ticketproduct.product)){
                    _discount += ticketproduct.amount * (this.discount.value || 0);    
                }
            }

            return Math.min((ticket.price || 0), _discount);
        }
    }

    private _prcntAmount(ticket: TicketView): number {
        if (this.discount.allprd){
            const _ticketprice = this._ticketPrice(ticket);
            if (_ticketprice){
                return Math.min(_ticketprice, _ticketprice * (this.discount.value || 0) / 100);
            }
            
            return 0;
        }
        else {
            const _appliesto = this.products;

            let _discount = 0;  // the discount value is applied per affected product
            for (const ticketproduct of ticket.products){
                if (ticketproduct.product && _appliesto.includes(ticketproduct.product)){
                    _discount += ticketproduct.amount * ((ticketproduct.charge || 0) * (this.discount.value || 0) / 100);    
                }
            }

            return Math.min((ticket.price || 0), _discount);
        }
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type DiscountView = ViewType <_DiscountView, Discount>;

export function DiscountViewProxy(object: Discount, data: dataService): DiscountView {
    return new _DiscountView(object, data).Proxy as DiscountView;
}
